import { useQuery } from '@tanstack/react-query';
import * as Combobox from '../../components/Combobox';
import { useRouteContext } from '@tanstack/react-router';
import { Destination } from '../../services/DestinationsApiClient';

import { useFormContext } from 'react-hook-form';
import { FormValues } from '.';
import { XApiClient } from '../../services/X';

type Props = {
  destinationMetadata: Destination<'x'>;
  output: Destination<'x'>['outputs'][number];
};

export function XBroadcastSourcesField({ output, destinationMetadata }: Props) {
  const { authContextProvider, serviceEnvironment } = useRouteContext({
    from: '/_authenticated/settings/_layout/broadcast-settings/',
  });

  const form = useFormContext<FormValues>();
  const value = form.watch(`outputs.${output.id}.xSourceId` as const) ?? '';

  const { data: streamKeys = new Map(), isLoading } = useQuery({
    queryKey: ['x', 'stream-sources', destinationMetadata.accessToken],
    queryFn: async () => {
      const xApiClient = new XApiClient({
        authContextProvider,
        serviceEnvironment,
        destinationMetadata,
      });

      return xApiClient.getStreamSources();
    },
    refetchInterval: false,
    refetchIntervalInBackground: false,
    staleTime: Infinity,
  });

  const streamKey = streamKeys.get(value) || {
    isStreamActive: false,
    name: 'Create new',
    sourceId: 'new',
    rtmpStreamKey: '',
    rtmpStreamUrl: '',
  };

  Object.assign(window, { form });

  return (
    <Combobox.ComboBoxRoot
      inputValue={isLoading ? 'Loading sources...' : (streamKey?.name ?? value ?? '')}
      value={[value]}
      onValueChange={([next]) => {
        form.setValue(`outputs.${output.id}.xSourceId`, next, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
      }}
    >
      <Combobox.ComboBoxTrigger
        label="Source"
        name={`outputs.${output.id}.xSourceId`}
        inputProps={{ readOnly: true }}
      />
      <Combobox.ComboBoxContent>
        <Combobox.ComboBoxOption value="new">Create new</Combobox.ComboBoxOption>
        {Array.from(streamKeys.entries()).map(([sourceId, { name }]) => (
          <Combobox.ComboBoxOption
            key={sourceId}
            value={sourceId}
            label={name}
            css={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
          >
            {name}
          </Combobox.ComboBoxOption>
        ))}
      </Combobox.ComboBoxContent>
    </Combobox.ComboBoxRoot>
  );
}
